/* Canvas de firma manuscrita reutilizable.
 *
 * Extraído del patrón de ServiciosEspecializadosDetalle: dibujo con pointer
 * events (mouse/touch/stylus) y exportación a PNG base64 vía toDataURL.
 * Uso:
 *   const firmaRef = useRef<FirmaCanvasHandle>(null);
 *   <FirmaCanvas ref={firmaRef} alto={160} />
 *   const png = firmaRef.current?.obtenerPng();   // "" si está vacío
 *   firmaRef.current?.limpiar();
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

export interface FirmaCanvasHandle {
  limpiar: () => void;
  /** PNG base64 del canvas. Cadena vacía si no se pudo generar. */
  obtenerPng: () => string;
  /**
   * PNG base64 recortado al trazo (con un margen). Sin el blanco de alrededor
   * la firma se ve grande cuando se muestra achicada, y pesa menos. Si no hay
   * trazo devuelve lo mismo que obtenerPng().
   */
  obtenerPngRecortado: (margen?: number) => string;
  /** True si el usuario dibujó algo desde el último limpiar(). */
  tieneTrazos: () => boolean;
}

interface Props {
  alto?: number;
  /**
   * Se llama al levantar el dedo despues de dibujar. Sirve para guardar la
   * firma en el estado del formulario sin esperar a un boton de confirmar.
   */
  onTrazo?: () => void;
}

const FirmaCanvas = forwardRef<FirmaCanvasHandle, Props>(({ alto = 160, onTrazo }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const tieneTrazosRef = useRef(false);

  useEffect(() => {
    const initCanvas = () => {
      const cv = canvasRef.current;
      if (!cv) return;
      const rect = cv.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      cv.width = Math.max(1, Math.floor(rect.width * dpr));
      cv.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = cv.getContext("2d");
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#111";
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
      }
    };
    // Doble init: al montar el layout puede no estar estable todavía (modales).
    requestAnimationFrame(initCanvas);
    const t = setTimeout(initCanvas, 80);
    return () => clearTimeout(t);
  }, []);

  const beginDraw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    e.preventDefault();
    cv.setPointerCapture?.(e.pointerId);
    pointerIdRef.current = e.pointerId;
    isDrawingRef.current = true;
    tieneTrazosRef.current = true;
    lastPosRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    const ctx = cv.getContext("2d");
    if (ctx) {
      ctx.beginPath();
      ctx.arc(lastPosRef.current.x, lastPosRef.current.y, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = "#111";
      ctx.fill();
    }
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const rect = cv.getBoundingClientRect();
    e.preventDefault();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const last = lastPosRef.current;
    if (!last) return;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPosRef.current = { x, y };
  };

  const endDraw = () => {
    const dibujaba = isDrawingRef.current;
    const cv = canvasRef.current;
    if (cv) {
      try {
        if (pointerIdRef.current !== null) {
          cv.releasePointerCapture?.(pointerIdRef.current);
        }
      } catch {
        // noop
      }
    }
    isDrawingRef.current = false;
    lastPosRef.current = null;
    pointerIdRef.current = null;
    if (dibujaba) onTrazo?.();
  };

  useImperativeHandle(ref, () => ({
    limpiar: () => {
      const cv = canvasRef.current;
      if (!cv) return;
      const ctx = cv.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, cv.width, cv.height);
      const rect = cv.getBoundingClientRect();
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
      tieneTrazosRef.current = false;
    },
    obtenerPng: () => {
      const cv = canvasRef.current;
      if (!cv) return "";
      return cv.toDataURL("image/png");
    },
    obtenerPngRecortado: (margen = 12) => {
      const cv = canvasRef.current;
      if (!cv) return "";
      const ctx = cv.getContext("2d");
      if (!ctx) return cv.toDataURL("image/png");
      const { width, height } = cv;
      const pixeles = ctx.getImageData(0, 0, width, height).data;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;
      // El fondo es blanco opaco: todo lo que no es casi blanco es trazo.
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const i = (y * width + x) * 4;
          if (pixeles[i] < 230 || pixeles[i + 1] < 230 || pixeles[i + 2] < 230) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < 0) return cv.toDataURL("image/png");
      const m = Math.round(margen * (window.devicePixelRatio || 1));
      const x0 = Math.max(0, minX - m);
      const y0 = Math.max(0, minY - m);
      const ancho = Math.min(width, maxX + m + 1) - x0;
      const alto = Math.min(height, maxY + m + 1) - y0;
      const recorte = document.createElement("canvas");
      recorte.width = ancho;
      recorte.height = alto;
      const rctx = recorte.getContext("2d");
      if (!rctx) return cv.toDataURL("image/png");
      rctx.drawImage(cv, x0, y0, ancho, alto, 0, 0, ancho, alto);
      return recorte.toDataURL("image/png");
    },
    tieneTrazos: () => tieneTrazosRef.current,
  }));

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: alto,
        border: "1px dashed #999",
        borderRadius: 8,
        background: "#fff",
        touchAction: "none",
        display: "block",
      }}
      onPointerDown={beginDraw}
      onPointerMove={draw}
      onPointerUp={endDraw}
      onPointerLeave={endDraw}
      onPointerCancel={endDraw}
    />
  );
});

FirmaCanvas.displayName = "FirmaCanvas";

export default FirmaCanvas;
